import { useMemo } from 'react';
import { motion } from 'framer-motion';

// ─── Types ──────────────────────────────────────────────────────────
export interface ProgressRingProps {
  current: number;
  target: number;
  label?: string;
}

// ─── Component ──────────────────────────────────────────────────────
export const ProgressRing = ({ current, target, label }: ProgressRingProps) => {
  const SIZE = 140;
  const STROKE_WIDTH = 10;
  const RADIUS = (SIZE - STROKE_WIDTH) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  // 目標値を動的に計算（現在の値が目標を超えたら次のステップへ自動引き上げ）
  const dynamicTarget = useMemo(() => {
    if (target <= 0) return 0;
    if (current < target) return target;
    // 例: target=10000, current=15000 -> nextTarget=20000
    const step = target; 
    return Math.ceil((current + 1) / step) * step;
  }, [current, target]);

  const percent = useMemo(
    () => (dynamicTarget > 0 ? Math.min(current / dynamicTarget, 1) : 0),
    [current, dynamicTarget],
  );

  const offset = CIRCUMFERENCE * (1 - percent);
  const gradientId = 'progressRingGrad';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="flex flex-col items-center gap-2"
    >
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-90"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00f3ff" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>

          {/* Background track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={STROKE_WIDTH}
          />

          {/* Progress arc */}
          <motion.circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            initial={{ strokeDashoffset: CIRCUMFERENCE }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
          />
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold font-display text-white">
            {Math.round(percent * 100)}%
          </span>
        </div>
      </div>

      {/* Below ring info */}
      <div className="flex flex-col items-center gap-0.5">
        <p className="text-xs text-gray-400">
          <span className="font-bold text-white">
            {current.toLocaleString()}
          </span>
          {' / '}
          <span className="text-gray-500">{dynamicTarget.toLocaleString()}</span>
        </p>
        {label && (
          <p className="text-[11px] font-medium tracking-wide text-gray-500">
            {label}{current >= target && ' (達成！次の目標へ)'}
          </p>
        )}
      </div>
    </motion.div>
  );
};
